// src/pages/job/JobList.js
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { removeJob, getJobs, updateJob } from "../../slice/jobSlice";
import AdminTable from "common/AdminTable";
import TruncatedMessage from "common/TruncatedMessage";
import { toast } from "react-toastify";

const JobList = () => {
  const dispatch = useDispatch();
  const { data: jobData } = useSelector((state) => state.job);

  const [showModal, setShowModal] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Load Jobs
  useEffect(() => {
    dispatch(getJobs());
  }, [dispatch]);

  // Handle Edit
  const handleEditClick = (job) => {
    setCurrentJob({ ...job });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentJob(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const requiredFields = [
      "jobTitle",
      "experience",
      "description",
      "skill",
      "education",
      "location",
    ];
    const hasEmptyRequired = requiredFields.some(
      (field) => !currentJob[field]?.trim()
    );

    if (hasEmptyRequired) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      await dispatch(updateJob({ id: currentJob.id, data: currentJob })).unwrap();
      toast.success("Job updated successfully");
      handleCloseModal();
    } catch (error) {
      console.error("Error updating job:", error);
      toast.error("Failed to update job.");
    }
  };

  // Table headers
  const tableHeaders = [
    { Header: "Job Title", accessor: "jobTitle" },
    { Header: "Experience", accessor: "experience" },
    { Header: "Skill", accessor: "skill" },
    { Header: "Education", accessor: "education" },
    { Header: "Location", accessor: "location" },
    {
      Header: "Description",
      accessor: "description",
      Cell: ({ row }) => (
        <TruncatedMessage text={row.original.description} maxLength={30} />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <>
          <button
            className="btn btn-info mx-1"
            onClick={() => {
              setSelectedJob(row.original);
              setViewModal(true);
            }}
          >
            View
          </button>
          <button
            className="btn removebtn mx-1"
            onClick={() => {
              if (window.confirm("Are you sure you want to remove this job?")) {
                dispatch(removeJob(row.original.id));
              }
            }}
          >
            Remove
          </button>
          <button
            className="btn editbtn mx-1"
            onClick={() => handleEditClick(row.original)}
          >
            Edit
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Job List</h4>
        </div>

        {jobData?.length > 0 && (
          <AdminTable tableHeaders={tableHeaders} tableData={jobData} />
        )}
      </div>

      {/* Edit Modal */}
      {showModal && currentJob && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Job</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <form>
                  <div className="row">
                    {[
                      { label: "Job Title", name: "jobTitle", type: "text" },
                      { label: "Experience", name: "experience", type: "text" },
                      { label: "Skill", name: "skill", type: "text" },
                      { label: "Education", name: "education", type: "text" },
                      { label: "Location", name: "location", type: "text" },
                    ].map(({ label, name, type }) => (
                      <div className="form-group col-md-6" key={name}>
                        <label htmlFor={`edit-${name}`}>{label}</label>
                        <input
                          type={type}
                          id={`edit-${name}`}
                          name={name}
                          className="form-control"
                          value={currentJob[name] || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                    ))}

                    <div className="form-group col-md-12">
                      <label htmlFor="edit-description">Description</label>
                      <textarea
                        id="edit-description"
                        name="description"
                        rows={4}
                        className="form-control"
                        value={currentJob.description || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal && selectedJob && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content border-0 shadow-lg rounded">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Job Details</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setViewModal(false)}
                ></button>
              </div>

              <div className="modal-body py-4 px-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <div className="row gy-3">
                  {[
                    { label: "Job Title", value: selectedJob.jobTitle },
                    { label: "Experience", value: selectedJob.experience },
                    { label: "Skill", value: selectedJob.skill },
                    { label: "Education", value: selectedJob.education },
                    { label: "Location", value: selectedJob.location },
                    { label: "Description", value: selectedJob.description },
                  ].map(({ label, value }, idx) => (
                    <div className="col-md-6" key={idx}>
                      <div className="form-group">
                        {/* For view-only, we can associate with a hidden input to satisfy the linter */}
                        <label htmlFor={`view-${label.replace(" ", "-")}`} className="fw-bold text-dark d-block mb-1">
                          {label}
                        </label>
                        <input
                          type="text"
                          id={`view-${label.replace(" ", "-")}`}
                          className="d-none"
                          value={value}
                          readOnly
                        />
                        <div className="text-secondary fs-6">{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobList;
